import { useState, useMemo, useEffect } from 'react'
import { sumBy } from 'lodash'

// API
import { useAxiosApi } from '@/hooks/useAxiosApi'

// Currency conversion rates (USD to EUR)
// In production, you might want to fetch this from an API or config
const USD_TO_EUR_RATE = 0.86

// Helper function to convert component value to EUR
const convertToEUR = (value, currency) => {
    if (!value) return 0
    if (currency === 'EUR') return value
    if (currency === 'USD') {
        // Convert and round to nearest 0.01 (cent)
        return Math.round(value * USD_TO_EUR_RATE * 100) / 100
    }

    return value
}

/*
 * useLineItems - Universal hook for managing line items (components OR shopify variants)
 *
 * Normalized line item structure:
 * {
 *   id: number,                          // Line item ID (if editing)
 *   item_type: 'component' | 'shopify_product',
 *   quantity: number,
 *   unit_price: number,
 *
 *   // Display fields (normalized)
 *   display_name: string,                // Full display name
 *   display_title: string,               // Primary title
 *   display_subtitle: string | null,     // Secondary title (category/variant)
 *   image_url: string | null,
 *   sku: string,
 *
 *   // Type-specific IDs
 *   component_id: number | null,
 *   shopify_product_variant_id: string | null,
 *   product_id: string | null,           // Shopify product ID (for variants)
 *
 *   // Computed fields
 *   total_price: number,
 *   availableAtLocation: number | null,
 *
 *   // Raw data
 *   _component: object | null,
 *   _variant: object | null,
 * }
 */

export const useLineItems = (initialLines = [], locationId = 1) => {
    const [lines, setLines] = useState([])
    const [loading, setLoading] = useState(false)
    const [components, setComponents] = useState([])
    const [variants, setVariants] = useState([])

    const { requestGet } = useAxiosApi()

    // Initialize from existing line items (for edit mode)
    useEffect(() => {
        if (initialLines.length > 0) {
            const normalizedLines = initialLines
                .map((line) => {
                    // Detect type from which FK is populated
                    const isComponent = !!line.component_id
                    const isVariant = !!line.shopify_product_variant_id

                    if (isComponent) {
                        return {
                            id: line.id,
                            item_type: 'component',
                            component_id: line.component_id,
                            shopify_product_variant_id: null,
                            quantity: line.total_quantity || line.quantity || 1,
                            unit_price:
                                line.unit_price_initial || line.unit_price || 0,
                        }
                    } else if (isVariant) {
                        return {
                            id: line.id,
                            item_type: 'shopify_product',
                            component_id: null,
                            shopify_product_variant_id:
                                line.shopify_product_variant_id ||
                                line.shopify_product_variant?.id,
                            quantity: line.total_quantity || line.quantity || 1,
                            unit_price:
                                line.unit_price_initial || line.unit_price || 0,
                        }
                    }

                    return null
                })
                .filter(Boolean)

            setLines(normalizedLines)
        }
    }, [initialLines])

    // Extract IDs for fetching
    const componentIds = useMemo(() => {
        return lines
            .filter(
                (line) => line.item_type === 'component' && line.component_id
            )
            .map((line) => line.component_id)
    }, [lines])

    const variantIds = useMemo(() => {
        return lines
            .filter(
                (line) =>
                    line.item_type === 'shopify_product' &&
                    line.shopify_product_variant_id
            )
            .map((line) => line.shopify_product_variant_id)
    }, [lines])

    // Fetch components
    const fetchComponents = async () => {
        if (componentIds.length === 0) return

        setLoading(true)

        try {
            const { data } = await requestGet('/components/by-ids', {
                ids: componentIds.join(','),
            })

            setComponents(data ?? [])
        } finally {
            setLoading(false)
        }
    }

    // Fetch variants
    const fetchVariants = async () => {
        if (variantIds.length === 0) return

        setLoading(true)

        try {
            const { data } = await requestGet('/shopify/variants/by-ids', {
                ids: variantIds.join(','),
            })

            setVariants(data ?? [])
        } finally {
            setLoading(false)
        }
    }

    // Fetch data when IDs change
    useEffect(() => {
        fetchComponents()
    }, [componentIds.join(',')])

    useEffect(() => {
        fetchVariants()
    }, [variantIds.join(',')])

    // Normalize component data
    const normalizeComponent = (component, line) => {
        const category = component.component_category
        const componentName = component.name_fr || component.name
        const categoryName = category?.name_fr || component.category
        const fullDisplayName = `${categoryName} — ${componentName}`

        // Calculate availability at location
        const physicalInvlevel =
            component.invlevels?.find(
                (iv) =>
                    iv.invtype === 'physical' && iv.location_id === locationId
            )?.invlevel || 0

        const holdQuantity =
            component.holds?.find((h) => h.location_id === locationId)
                ?.hold_quantity || 0

        const availableAtLocation = physicalInvlevel - holdQuantity

        return {
            ...line,
            display_name: fullDisplayName,
            display_title: fullDisplayName,
            display_subtitle: null,
            image_url: component.image_url,
            sku:
                component.sku ||
                `${category?.sku_prefix || 'XX'}${String(component.id).padStart(
                    4,
                    '0'
                )}`,
            total_price: line.unit_price * line.quantity,
            availableAtLocation,
            _component: component,
        }
    }

    // Normalize variant data
    const normalizeVariant = (variant, line) => {
        const product = variant.shopify_product
        const displayTitle = product.title
        const displaySubtitle =
            variant.title !== 'Default Title' ? variant.title : null

        // Calculate availability (you can extend this with getAugmentedVariant if needed)
        const availableAtLocation = variant.quantity_available || null

        return {
            ...line,
            display_name: `${displayTitle}${
                displaySubtitle ? ' — ' + displaySubtitle : ''
            }`,
            display_title: displayTitle,
            display_subtitle: displaySubtitle,
            image_url: variant.image_url,
            sku: variant.sku,
            product_id: product.id,
            total_price: line.unit_price * line.quantity,
            availableAtLocation,
            _variant: variant,
        }
    }

    // Augmented lines with full display data
    const augmentedLines = useMemo(() => {
        return lines
            .map((line) => {
                if (line.item_type === 'component') {
                    const component = components.find(
                        (c) => c.id === line.component_id
                    )
                    if (!component) return null
                    return normalizeComponent(component, line)
                } else if (line.item_type === 'shopify_product') {
                    const variant = variants.find(
                        (v) => v.id === line.shopify_product_variant_id
                    )
                    if (!variant) return null
                    return normalizeVariant(variant, line)
                }
                return null
            })
            .filter(Boolean)
    }, [lines, components, variants, locationId])

    // Add a component
    const addComponent = async (componentId) => {
        // Check if already exists
        const existingLineIndex = lines.findIndex(
            (l) => l.component_id === componentId
        )

        if (existingLineIndex !== -1) {
            // Increment quantity
            updateLineQuantity(
                existingLineIndex,
                lines[existingLineIndex].quantity + 1
            )
            return
        }

        // Fetch component details
        const { data: components } = await requestGet('/components/by-ids', {
            ids: String(componentId),
        })

        const component = components?.[0]

        if (!component) return

        // Convert price to EUR if necessary
        const priceInEUR = convertToEUR(component.value, component.value_currency)

        // Add new line
        setLines([
            ...lines,
            {
                item_type: 'component',
                component_id: componentId,
                shopify_product_variant_id: null,
                quantity: 1,
                unit_price: priceInEUR,
            },
        ])
    }

    // Add a variant
    const addVariant = async (variantId) => {
        // Check if already exists
        const existingLineIndex = lines.findIndex(
            (l) => l.shopify_product_variant_id === variantId
        )

        if (existingLineIndex !== -1) {
            // Increment quantity
            updateLineQuantity(
                existingLineIndex,
                lines[existingLineIndex].quantity + 1
            )
            return
        }

        // Fetch variant details
        const { data: variant } = await requestGet(
            `/shopify/variants/${variantId}`
        )

        if (!variant) return

        // Add new line
        setLines([
            ...lines,
            {
                item_type: 'shopify_product',
                component_id: null,
                shopify_product_variant_id: variantId,
                quantity: 1,
                unit_price: variant.price || 0,
            },
        ])
    }

    // Update line quantity
    const updateLineQuantity = (lineIndex, quantity) => {
        setLines(
            lines.map((line, index) =>
                index === lineIndex ? { ...line, quantity } : line
            )
        )
    }

    // Update line price
    const updateLinePrice = (lineIndex, price) => {
        setLines(
            lines.map((line, index) =>
                index === lineIndex ? { ...line, unit_price: price } : line
            )
        )
    }

    // Remove a line
    const removeLine = (lineIndex) => {
        setLines(lines.filter((line, index) => index !== lineIndex))
    }

    // Totals
    const total_without_shipping = useMemo(() => {
        return sumBy(augmentedLines, 'total_price')
    }, [augmentedLines])

    const total_quantity = useMemo(() => {
        return sumBy(augmentedLines, 'quantity')
    }, [augmentedLines])

    return {
        lines: augmentedLines,
        loading,
        addComponent,
        addVariant,
        updateLineQuantity,
        updateLinePrice,
        removeLine,
        total_without_shipping,
        total_quantity,
    }
}
